import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { useLive, useNow, useRuntime } from './context';
import { useHasModule, useRemote } from './useRemote';
import { ApiFailure } from '../api/errors';
import { stageReservationForTill } from './till';
import { formatFreshness, formatMoney } from '../mirror/time';
import { Reservation, ReservationList, ReservationStatus } from '../wire';
import type { Reservation as ReservationType } from '../wire';
import { useLocale, useT } from '../messages';
import type { Messages } from '../messages';

/**
 * **The shelf** (`docs/lovespa-gaps-plan.md` C1) — what guests have reserved and
 * what the shop owes them.
 *
 * ## Why this is not the Orders screen
 *
 * The pass is a **day**: tickets arrive, are cooked and leave inside one
 * service, so that screen opens on today and walks days. A shelf is a **state**:
 * a jar waits until somebody comes in for it, which might be Thursday, and the
 * owner's question is *what is waiting*, not *what is today*. So this screen
 * opens on the open states and has no date picker at all — the list is short by
 * construction, because a shop with fifty things waiting has a different problem.
 *
 * ## What it does not do
 *
 * It does not take the money. "Collected" marks the shelf; the till is
 * Checkout, and the two are deliberately separate taps — a card reader that
 * would not open must not leave the shelf thinking a jar is still there, and a
 * jar that has gone has to be recorded as gone even when the guest pays by
 * Venmo on a phone somebody is still looking for.
 */

/** The three lanes the shop actually looks at, in the order it looks at them. */
const LANES = [
  { status: 'placed', label: 'reservations.lanePlaced' },
  { status: 'ready', label: 'reservations.laneReady' },
  { status: 'collected', label: 'reservations.laneCollected' },
] as const satisfies readonly { status: ReservationStatus; label: keyof Messages }[];

const STATUS_VERB = {
  placed: 'reservations.verbPlaced',
  ready: 'reservations.verbReady',
  collected: 'reservations.verbCollected',
  cancelled: 'reservations.verbCancelled',
  expired: 'reservations.verbExpired',
} as const satisfies Record<ReservationStatus, keyof Messages>;

interface Refusal {
  reservationId: string;
  message: string;
  allowed: ReservationStatus[] | null;
}

export function Reservations(): ReactNode {
  const runtime = useRuntime();
  useNow();
  const now = runtime.now();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';

  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  /** Which reservation was just handed to the till, for the one-line hint. */
  const [stagedNote, setStagedNote] = useState<string | null>(null);
  const entitled = useHasModule('products');

  const reservations = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/reservations',
        query: { limit: 200 },
        schema: ReservationList,
        signal,
      }),
    [],
    {
      cue: (type) => type.startsWith('reservation.'),
      pollMs: 30_000,
      enabled: entitled,
    },
  );

  const move = useCallback(
    async (reservation: ReservationType, status: ReservationStatus): Promise<void> => {
      setBusy(reservation.reservation_id);
      setRefusal(null);
      try {
        await runtime.api.request({
          method: 'POST',
          path: `/reservations/${reservation.reservation_id}/status`,
          body: {
            status,
            ...(session === null ? {} : { tenant_id: session.tenant.tenant_id }),
          },
          idempotencyKey: ulid(),
          schema: Reservation,
        });
      } catch (e) {
        if (e instanceof ApiFailure && e.status === 409) {
          // The server's own list of what is legal from where it *is* — the
          // screen never holds a second copy of the transition table.
          const allowed = Array.isArray((e.detail as { allowed?: unknown } | null)?.allowed)
            ? ((e.detail as { allowed: unknown[] }).allowed.filter(
                (value): value is ReservationStatus =>
                  ReservationStatus.options.includes(value as ReservationStatus),
              ) as ReservationStatus[])
            : null;
          setRefusal({ reservationId: reservation.reservation_id, message: e.message, allowed });
        } else {
          setRefusal({
            reservationId: reservation.reservation_id,
            message: e instanceof Error ? e.message : String(e),
            allowed: null,
          });
        }
      } finally {
        setBusy(null);
        reservations.reload();
      }
    },
    [reservations, runtime.api, session],
  );

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('reservations.notSwitchedOn')}</p>
      </section>
    );
  }

  const rows = reservations.data?.reservations ?? [];
  const open = rows.filter((row) => row.status === 'placed' || row.status === 'ready');
  const closed = rows.filter((row) => row.status === 'cancelled' || row.status === 'expired');

  return (
    <section className="view view--orders">
      <div className="view__header">
        <h1>{t('reservations.title')}</h1>
        <span className="status-strip__staleness">
          {`${formatFreshness(reservations.fetchedAt, now, timezone, locale)}${
            reservations.error === null ? '' : t('reservations.couldNotReach')
          }`}
        </span>
      </div>

      {reservations.loading && reservations.fetchedAt === null ? (
        <p className="empty">{t('reservations.reading')}</p>
      ) : null}
      {!reservations.loading && rows.length === 0 ? (
        <p className="empty">{t('reservations.none')}</p>
      ) : null}

      <div className="lanes">
        {LANES.map((lane) => {
          const inLane = (lane.status === 'collected' ? rows : open).filter(
            (row) => row.status === lane.status,
          );
          return (
            <section className={`lane lane--${lane.status}`} key={lane.status}>
              <header className="lane__head">
                <h2>{t(lane.label)}</h2>
                <span className="lane__count">{inLane.length}</span>
              </header>
              <ul className="lane__list">
                {inLane.length === 0 ? <li className="lane__empty">—</li> : null}
                {inLane.map((row) => (
                  <li className="ticket" key={row.reservation_id}>
                    <p className="ticket__open">
                      <strong>{row.client_name ?? t('reservations.someone')}</strong>
                      {' · '}
                      {formatMoney(row.outstanding_cents, currency, locale)}
                      {row.payment === 'at_pickup' ? ` ${t('reservations.atPickup')}` : ''}
                    </p>
                    <ul className="checkout-lines">
                      {row.lines.map((line) => (
                        <li key={line.line_id}>
                          <span className="checkout-lines__qty">
                            {t('reservations.qty', { qty: line.qty })}
                          </span>
                          <span className="checkout-lines__what">{line.name}</span>
                          <span className="checkout-lines__price">
                            {formatMoney(line.line_total_cents, currency, locale)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {row.notes === '' ? null : <p className="note">{row.notes}</p>}
                    {row.expires_at === null ? null : (
                      <p className="muted">
                        {t('reservations.heldUntil', {
                          date: new Date(row.expires_at).toLocaleDateString(locale, {
                            timeZone: timezone,
                            month: 'short',
                            day: 'numeric',
                          }),
                        })}
                      </p>
                    )}
                    {refusal !== null && refusal.reservationId === row.reservation_id ? (
                      <p className="ticket__refusal error" role="alert">
                        {refusal.message}
                      </p>
                    ) : null}
                    <div className="row-actions">
                      {buttonsFor(row, refusal).map((status) => (
                        <button
                          key={status}
                          type="button"
                          className={
                            status === 'cancelled'
                              ? 'link-button link-button--danger'
                              : 'link-button'
                          }
                          disabled={busy === row.reservation_id}
                          onClick={() => void move(row, status)}
                        >
                          {t(STATUS_VERB[status])}
                        </button>
                      ))}
                      {row.status === 'collected' ? (
                        /*
                         * **The hand-over to the till.** This screen records
                         * that the jar left the shelf; the money is rung up
                         * where money is always rung up — Checkout, over §6.5's
                         * settlement document with its own waterfall and its own
                         * audit row. A second implementation of "what was
                         * charged" is what §7.4 rule 3 exists to prevent.
                         *
                         * It **stages** the reservation rather than linking to
                         * it: this app has no router (`App.tsx` holds a tab in
                         * state), so a URL would be a link that looks like it
                         * works. `ui/till.ts` is the seam, and the sentence
                         * beside the button is what tells the cashier to press
                         * Checkout.
                         */
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            stageReservationForTill(row.reservation_id);
                            setStagedNote(row.reservation_id);
                          }}
                        >
                          {t('reservations.ringUp')}
                        </button>
                      ) : null}
                      {stagedNote === row.reservation_id ? (
                        <span className="muted">{t('reservations.ringUpHint')}</span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {closed.length === 0 ? null : (
        <section className="lane lane--cancelled">
          <header className="lane__head">
            <h2>{t('reservations.laneClosed')}</h2>
            <span className="lane__count">{closed.length}</span>
          </header>
          <ul className="lane__list lane__list--cancelled">
            {closed.map((row) => (
              <li className="ticket ticket--cancelled" key={row.reservation_id}>
                <p className="ticket__open">
                  <strong>{row.client_name ?? t('reservations.someone')}</strong>
                  {' · '}
                  {t(STATUS_VERB[row.status])}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="muted">{t('reservations.tillNote')}</p>
    </section>
  );
}

/**
 * The buttons, from the **server's** answer — `next_statuses` on the row, or the
 * `allowed` list a refusal came back with when this screen is a moment behind.
 *
 * `Orders.tsx`'s rule, and its reason: a screen that held its own copy of the
 * transition table would eventually offer a move the server refuses, and the
 * person tapping it is standing in front of a guest.
 */
function buttonsFor(row: ReservationType, refusal: Refusal | null): ReservationStatus[] {
  if (
    refusal !== null &&
    refusal.reservationId === row.reservation_id &&
    refusal.allowed !== null
  ) {
    return refusal.allowed;
  }
  return row.next_statuses.filter((status): status is ReservationStatus =>
    ReservationStatus.options.includes(status as ReservationStatus),
  );
}
